import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CrudService } from '@app/api/api-services/crud.service.base';
import { HttpClientService } from '@app/api/api-services/http-client.service';
import {
  ProgramSkillApiModel,
  ProgramSkillUsageApiModel,
  SiteSkillBaseApiModel,
  ProgramSkillsSelectorsApiModel,
  ProgramSettingsUsageInfoApiModel,
  ProgramSkillsRequestApiModel
} from '@app/api/api-models';
import { toQueryParams } from '@app/shared/utils';
import { sanitizeHtml } from '@shared/utils/editor-utils';

@Injectable()
export class ProgramSkillsService extends CrudService<ProgramSkillApiModel, SiteSkillBaseApiModel, number> {
  public apiUrl = 'program-skills';

  constructor(public http: HttpClientService) {
    super(http);
  }

  public getList(request?: ProgramSkillsRequestApiModel): Observable<ProgramSkillApiModel[]> {
    return this.http.get<ProgramSkillApiModel[]>(`${this.apiUrl}`, { params: toQueryParams(request) });
  }

  public getDetails(id: number): Observable<ProgramSkillUsageApiModel> {
    return this.http.get<ProgramSkillUsageApiModel>(`${this.apiUrl}/${id}`);
  }

  public getSelectors(): Observable<ProgramSkillsSelectorsApiModel> {
    return this.http
      .get<ProgramSkillsSelectorsApiModel>(`${this.apiUrl}/selectors`)
      .pipe(map((x) => new ProgramSkillsSelectorsApiModel().deserialize(x)));
  }

  public add(model: ProgramSkillApiModel): Observable<ProgramSkillApiModel> {
    model.Description = sanitizeHtml(model.Description);
    return super.add(model);
  }

  public update(model: ProgramSkillApiModel): Observable<ProgramSkillApiModel> {
    model.Description = sanitizeHtml(model.Description);
    return super.update(model);
  }

  public getPrint(request: ProgramSkillsRequestApiModel): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/print`, { params: toQueryParams(request), responseType: 'blob' });
  }

  public getUsageInfo(id: number): Observable<ProgramSettingsUsageInfoApiModel> {
    return this.http.get<ProgramSettingsUsageInfoApiModel>(`${this.apiUrl}/${id}/usage-info`);
  }

  public getUnused(): Observable<SiteSkillBaseApiModel[]> {
    return this.http.get<SiteSkillBaseApiModel[]>(`${this.apiUrl}/unused`);
  }

  public addBatch(ids: number[]): Observable<number[]> {
    return this.http.post<number[]>(`${this.apiUrl}/add-batch`, ids);
  }

  public getImportTemplateExample(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/get-import-template-example`, { responseType: 'blob' });
  }

  public getExistingSkill(payload: ProgramSkillApiModel): Observable<ProgramSkillApiModel> {
    return this.http.post<ProgramSkillApiModel>(`${this.apiUrl}/existing`, payload);
  }
}
